// src/hooks/useArticle.ts
import { useState, useEffect } from 'react';
import { apiPublicGet } from '../api';
import { ArticleItem } from '../types/helpArticles';
import { prerenderedArticle } from '../utils/prerenderedHelp';

export function useArticle(slug: string): {
  article: ArticleItem | null;
  loading: boolean;
  error: string | null;
} {
  const [article, setArticle] = useState<ArticleItem | null>(() => prerenderedArticle(slug));
  const [loading, setLoading] = useState(() => !prerenderedArticle(slug));
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (!slug) return;
    let cancelled = false;
    // Built into the page's HTML (#1143): show it now and refresh quietly behind it.
    const seeded = prerenderedArticle(slug);
    setArticle(seeded);
    setLoading(!seeded);
    setError(null);

    apiPublicGet(`/articles/${slug}`)
      .then(async (res) => {
        const data = await res.body.json() as unknown as ArticleItem;
        if (!cancelled) setArticle(data);
      })
      .catch((err) => {
        console.error('[useArticle] fetch failed:', err);
        // A failed refresh keeps the built copy, which is a real article.
        if (!cancelled && !seeded) setError('Failed to load article');
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });

    return () => { cancelled = true; };
  }, [slug]);

  return { article, loading, error };
}
